import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Loader2, ShieldAlert, Lock } from "lucide-react";

export default function ForcePasswordChange() {
  const { refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("Nova lozinka mora imati najmanje 8 karaktera.");
      return;
    }
    if (next !== confirm) {
      toast.error("Lozinke se ne poklapaju.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      await refresh();
      toast.success("Lozinka uspešno promenjena.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md nasa-card p-6 md:p-8" data-testid="force-password-card">
        <div className="flex justify-center mb-6">
          <Logo size={48} withText />
        </div>

        <div className="border border-[#ff7a18]/40 bg-[#ff7a18]/5 p-4 rounded-sm mb-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#ff7a18] mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-bold text-[#ff7a18] text-sm uppercase tracking-widest">
              Promena lozinke obavezna
            </div>
            <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
              Iz bezbednosnih razloga, morate postaviti novu lozinku pre nego što nastavite. Ovaj korak je obavezan na prvom prijavljivanju.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field
            label="Trenutna lozinka"
            value={current}
            onChange={setCurrent}
            testid="current-password"
            autoFocus
          />
          <Field
            label="Nova lozinka (min. 8 karaktera)"
            value={next}
            onChange={setNext}
            testid="new-password"
          />
          <Field
            label="Potvrdite novu lozinku"
            value={confirm}
            onChange={setConfirm}
            testid="confirm-password"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit"
            className="w-full flex items-center justify-center gap-2 bg-[#22ff77] text-black font-bold py-3 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all glow-green disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Postavi novu lozinku
          </button>

          <button
            type="button"
            onClick={() => logout()}
            className="w-full text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-[#ff7a18] pt-2"
          >
            Odjavi se
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, autoFocus = false }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</label>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        autoFocus={autoFocus}
        autoComplete="off"
        className="mt-1 w-full bg-black border border-white/10 px-3 py-3 text-sm rounded-sm focus:outline-none focus:border-[#22ff77]"
      />
    </div>
  );
}
